import * as React from 'react';
import { ChevronDown, ChevronRight, ChevronDownSquare, ChevronUpSquare } from 'lucide-react';
import { AccountEntry, CategoryTemplate, Project, ViewType } from '../../types';
import { ViewToggle } from '../ui/ViewToggle';
import { 
  SectionHeader, 
  CategoryRow, 
  SectionTotal 
} from './ComponentsDetailed';
import { EBITDABridgeDetailed } from './EBITDABridgeDetailed';
import {
  getQuarter,
  getYear,
  getYearMonthRange,
  monthMap,
  compareMonthYear,
  calculateGrowthPercentage,
  calculateTTMYoYGrowth,
  calculateYTDGrowthPercentage,
  calculateSectionTotal,
  calculateCategoryTotal,
  calculateTTM,
  calculateTTMForPeriodType
} from '../../lib/financial/calculations';
import { FrontendPLCalculationService } from '../../lib/financial/frontendPLCalculations';
import { 
  getAllFinancialReports,
  getAvailableMonths,
  compareMonthYear as compareMonthYearHelper
} from '../../lib/data/financialReportsHelper';
import { getDateForPeriod } from '../../lib/utils/dateMapping';

interface UnifiedDetailedIncomeStatementProps {
  accounts: AccountEntry[];
  activeTemplate?: CategoryTemplate | null;
  selectedProject?: Project | null;
}

// ViewType now imported from types.ts

// Modified state management for expanded categories
interface ExpandedState {
  sections: Record<string, boolean>;
  categories: Record<string, boolean>;
}

// Interface for backend data structure
interface PLLineItem {
  type: 'section_header' | 'category_header' | 'account' | 'category_total' | 'section_total' | 'calculated_total' | 'calculated_percentage';
  name: string;
  values?: number[];
  level: number;
  category_value?: string;
  account_number?: string;
  is_final?: boolean;
}

interface PLDetailData {
  periods: string[];
  line_items: PLLineItem[];
}

const UnifiedDetailedIncomeStatementInner = (
  { accounts, activeTemplate, selectedProject }: UnifiedDetailedIncomeStatementProps,
  ref: React.Ref<void>
) => {
  // Debug: Track component renders and selectedProject changes
  React.useEffect(() => {
    console.log('🔄 [Income Statement] Component rendered/re-rendered with selectedProject:', {
      id: selectedProject?.id,
      period_type: selectedProject?.period_type,
      name: selectedProject?.name
    });
  });
  // Initialize viewType based on project period type if available
  const getInitialViewType = (): ViewType => {
    console.log('[DEBUG] getInitialViewType called with selectedProject:', {
      id: selectedProject?.id,
      period_type: selectedProject?.period_type,
      typeof_period_type: typeof selectedProject?.period_type
    });
    
    if (selectedProject?.period_type === 'annual') return 'yearly';
    if (selectedProject?.period_type === 'quarterly') return 'quarterly';
    return 'monthly';
  };

  const [viewType, setViewType] = React.useState<ViewType>(getInitialViewType());
  const [expandedState, setExpandedState] = React.useState<ExpandedState>({
    sections: {
      'Revenue': true,
      'Sales Discounts and Returns': true,
      'Cost of Goods Sold': true,
      'Operating Expenses': true,
      'Other Expenses': true
    },
    categories: {}
  });
  const [plDetailData, setPLDetailData] = React.useState<PLDetailData | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [pendingExpandAll, setPendingExpandAll] = React.useState(false);

  // Frontend calculation mode - use frontend calculations by default
  const [calculationSource, setCalculationSource] = React.useState<'frontend' | 'backend'>('frontend');

  // Generate frontend P&L data
  const frontendPLData = React.useMemo(() => {
    if (calculationSource === 'frontend' && accounts.length > 0 && activeTemplate) {
      try {
        return FrontendPLCalculationService.buildPLData(accounts, activeTemplate, viewType, selectedProject);
      } catch (err) {
        console.error('Error generating frontend P&L data:', err);
        return null;
      }
    }
    return null;
  }, [calculationSource, accounts, activeTemplate, viewType, selectedProject]);

  // Load P&L data from backend (only when using backend calculations or as fallback)
  React.useEffect(() => {
    const loadPLData = async () => {
      // Only load backend data if using backend calculations or if frontend data is not available
      if (!selectedProject?.id || (calculationSource === 'frontend' && frontendPLData)) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const reports = await getAllFinancialReports(selectedProject.id);

        if (!reports.pnl_detail?.data) {
          // If frontend data is available, don't show error
          if (calculationSource === 'frontend' && frontendPLData) {
            setLoading(false);
            return;
          }
          setError('P&L detail report not found. Please generate reports first.');
          return;
        }

        // Backend data is already in the correct format
        setPLDetailData(reports.pnl_detail.data);

      } catch (err) {
        console.error('Error loading P&L data:', err);
        // If frontend data is available, don't show error
        if (calculationSource === 'frontend' && frontendPLData) {
          setLoading(false);
          return;
        }
        setError('Failed to load P&L data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadPLData();
  }, [selectedProject?.id, calculationSource, frontendPLData]);

  // Auto-select appropriate view based on project period type
  React.useEffect(() => {
    if (selectedProject?.period_type) {
      const periodType = selectedProject.period_type;
      const availableViews: ViewType[] = 
        periodType === 'quarterly' ? ['quarterly', 'yearly'] :
        periodType === 'annual' ? ['yearly'] :
        ['monthly', 'quarterly', 'yearly'];
      
      // If current view is not available for this period type, switch to first available
      if (!availableViews.includes(viewType)) {
        setViewType(availableViews[0]);
      }
    }
  }, [selectedProject?.period_type]);

  // Helper function to get value from line items by name
  const getLineItemValue = (lineItemName: string, period: string, transformedData: PLDetailData): number => {
    const lineItem = transformedData.line_items.find(item => item.name === lineItemName);
    if (!lineItem?.values) return 0;
    
    const periodIndex = transformedData.periods.indexOf(period);
    if (periodIndex === -1) return 0;
    
    return lineItem.values[periodIndex] || 0;
  };

  // Transform monthly data to quarterly/yearly based on view type
  const transformDataForView = React.useCallback((data: PLDetailData | null): PLDetailData | null => {
    if (!data) return null;
    
    if (viewType === 'monthly') {
      return data; // Return monthly data as-is
    }
    
    const monthlyPeriods = data.periods;
    const allMonths = [...monthlyPeriods].sort(compareMonthYearHelper);
    
    // Generate target periods based on view type
    const targetPeriods = viewType === 'quarterly'
      ? [...new Set(allMonths.map(getQuarter))].sort((a, b) => {
          const [q1, y1] = a.split("'");
          const [q2, y2] = b.split("'");
          return y1 === y2 
            ? parseInt(q1.slice(1)) - parseInt(q2.slice(1)) 
            : parseInt(y1) - parseInt(y2);
        })
      : [...new Set(allMonths.map(getYear))].sort();
    
    // Helper to get months for a period
    const getMonthsForPeriod = (period: string): string[] => {
      if (viewType === 'quarterly') {
        const [q, year] = period.split("'");
        const quarterNum = parseInt(q.slice(1));
        const expectedMonths: string[] = [];
        const startMonth = (quarterNum - 1) * 3;
        for (let i = 0; i < 3; i++) {
          const monthNum = startMonth + i;
          const month = Object.keys(monthMap)[monthNum];
          expectedMonths.push(`${month}-${year}`);
        }
        return allMonths.filter(month => expectedMonths.includes(month));
      } else {
        // Yearly - get all months for that year
        return allMonths.filter(month => getYear(month) === period);
      }
    };
    
    // Transform line items
    const transformedLineItems: PLLineItem[] = data.line_items.map(item => {
      if (!item.values || item.type === 'section_header' || item.type === 'category_header') {
        // Headers don't have values to transform
        return item;
      }
      
      const newValues: number[] = targetPeriods.map(period => {
        const monthsInPeriod = getMonthsForPeriod(period);
        
        // Sum values for months in this period
        return monthsInPeriod.reduce((sum, month) => {
          const monthIndex = monthlyPeriods.indexOf(month);
          if (monthIndex !== -1 && item.values) {
            return sum + (item.values[monthIndex] || 0);
          }
          return sum;
        }, 0);
      });
      
      return {
        ...item,
        values: newValues
      };
    });
    
    return {
      periods: targetPeriods,
      line_items: transformedLineItems
    };
  }, [viewType]);

  // Get data source based on calculation mode
  const currentDataSource = calculationSource === 'frontend' && frontendPLData
    ? frontendPLData
    : plDetailData;

  // Get transformed data for current view
  const transformedData = transformDataForView(currentDataSource);

  // Auto-expand all after data loads if pendingExpandAll is set
  React.useEffect(() => {
    if (pendingExpandAll && currentDataSource) {
      expandAll();
    }
  }, [pendingExpandAll, currentDataSource]);

  // Set all categories collapsed on data load
  React.useEffect(() => {
    if (currentDataSource) {
      const newCategories: Record<string, boolean> = {};
      currentDataSource.line_items
        .filter(item => item.type === 'category_header' && item.category_value)
        .forEach(item => {
          if (item.category_value) {
            newCategories[item.category_value] = false;
          }
        });
      setExpandedState(prev => ({
        ...prev,
        categories: newCategories
      }));
    }
  }, [currentDataSource]);

  // Helper functions to get calculated values from backend data
  const calculateNetRevenueTotal = (period: string) => {
    if (!transformedData) return 0;
    return getLineItemValue('Net Revenue', period, transformedData);
  };

  const calculateGrossMargin = (period: string) => {
    if (!transformedData) return 0;
    return getLineItemValue('Gross Margin', period, transformedData);
  };

  const calculateOperatingIncome = (period: string) => {
    if (!transformedData) return 0;
    return getLineItemValue('Operating Income', period, transformedData);
  };

  const calculateNetIncome = (period: string) => {
    if (!transformedData) return 0;
    return getLineItemValue('Net Income', period, transformedData);
  };

  // TTM calculations for yearly view - period-aware using same logic as calculateTTMForPeriodType
  const calculateTTMNetRevenue = () => {
    if (viewType !== 'yearly') return 0;

    // Use frontend calculations when available
    if (calculationSource === 'frontend' && frontendPLData && accounts.length > 0 && activeTemplate) {
      const ttmValues = FrontendPLCalculationService.calculateTTMValues(accounts, activeTemplate, frontendPLData.periods, selectedProject);
      return ttmValues.ttmNetRevenue;
    }

    // Fallback to backend data
    if (!currentDataSource) return 0;
    const projectPeriodType = selectedProject?.period_type || 'monthly';
    
    // Use the same TTM logic as individual categories: sum revenue categories using calculateTTMForPeriodType
    const allMonths = currentDataSource.periods;
    const revenueCategories = ['Sales Revenue']; // Primary revenue category
    const contraRevenueCategories = ['Sales Returns and Allowances', 'Sales Discounts']; // Contra-revenue categories

    const revenueTotal = revenueCategories.reduce((total, category) => {
      return total + calculateTTMForPeriodType(
        accounts,
        category,
        allMonths,
        true,  // isRevenue
        false, // isContraRevenue
        projectPeriodType
      );
    }, 0);

    const contraRevenueTotal = contraRevenueCategories.reduce((total, category) => {
      return total + calculateTTMForPeriodType(
        accounts,
        category,
        allMonths,
        true, // isRevenue
        true, // isContraRevenue
        projectPeriodType
      );
    }, 0);

    return revenueTotal + contraRevenueTotal; // contraRevenue is already negative from calculateTTMForPeriodType
  };

  const calculateTTMGrossMargin = () => {
    if (viewType !== 'yearly') return 0;

    // Use frontend calculations when available
    if (calculationSource === 'frontend' && frontendPLData && accounts.length > 0 && activeTemplate) {
      const ttmValues = FrontendPLCalculationService.calculateTTMValues(accounts, activeTemplate, frontendPLData.periods, selectedProject);
      return ttmValues.ttmGrossMargin;
    }

    // Fallback to backend data
    if (!currentDataSource) return 0;

    // Gross Margin = Net Revenue - Cost of Goods Sold
    const ttmNetRevenue = calculateTTMNetRevenue();
    const cogsCategories = getSectionCategories('Cost of Goods Sold');
    const ttmCOGS = calculateTTMSectionTotal(cogsCategories, false, false);

    return ttmNetRevenue - ttmCOGS;
  };

  const calculateTTMOperatingIncome = () => {
    if (viewType !== 'yearly') return 0;

    // Use frontend calculations when available
    if (calculationSource === 'frontend' && frontendPLData && accounts.length > 0 && activeTemplate) {
      const ttmValues = FrontendPLCalculationService.calculateTTMValues(accounts, activeTemplate, frontendPLData.periods, selectedProject);
      return ttmValues.ttmOperatingIncome;
    }

    // Fallback to backend data
    if (!currentDataSource) return 0;

    // Operating Income = Gross Margin - Operating Expenses
    const ttmGrossMargin = calculateTTMGrossMargin();
    const operatingExpenseCategories = getSectionCategories('Operating Expenses');
    const ttmOperatingExpenses = calculateTTMSectionTotal(operatingExpenseCategories, false, false);

    return ttmGrossMargin - ttmOperatingExpenses;
  };

  const calculateTTMNetIncome = () => {
    if (viewType !== 'yearly') return 0;

    // Use frontend calculations when available
    if (calculationSource === 'frontend' && frontendPLData && accounts.length > 0 && activeTemplate) {
      const ttmValues = FrontendPLCalculationService.calculateTTMValues(accounts, activeTemplate, frontendPLData.periods, selectedProject);
      return ttmValues.ttmNetIncome;
    }

    // Fallback to backend data
    if (!currentDataSource) return 0;

    // Net Income = Operating Income - Other Expenses + Other Income
    const ttmOperatingIncome = calculateTTMOperatingIncome();
    const otherExpenseCategories = getSectionCategories('Other Expenses');
    const ttmOtherExpenses = calculateTTMSectionTotal(otherExpenseCategories, false, false);

    return ttmOperatingIncome - ttmOtherExpenses;
  };

  // Helper function to calculate TTM for section totals using the same logic as individual line items
  const calculateTTMSectionTotal = (categories: string[], isRevenue: boolean, isContraRevenue: boolean) => {
    if (!currentDataSource || viewType !== 'yearly') return 0;
    const projectPeriodType = selectedProject?.period_type || 'monthly';

    // Use the same approach as individual line items: sum up TTM for each category
    const allMonths = currentDataSource.periods;

    return categories.reduce((total, category) => {
      return total + calculateTTMForPeriodType(
        accounts,
        category,
        allMonths,
        isRevenue,
        isContraRevenue,
        projectPeriodType
      );
    }, 0);
  };

  const expandAll = () => {
    if (!currentDataSource) {
      setPendingExpandAll(true);
      return;
    }
    const newState: ExpandedState = {
      sections: {},
      categories: {}
    };
    // Expand all sections
    Object.keys(expandedState.sections).forEach(section => {
      newState.sections[section] = true;
    });
    // Expand all categories from data
    currentDataSource.line_items
        .filter(item => item.type === 'category_header' && item.category_value)
        .forEach(item => {
          if (item.category_value) {
            newState.categories[item.category_value] = true;
          }
        });
    setExpandedState(newState);
    setPendingExpandAll(false);
  };

  const collapseAll = () => {
    if (!currentDataSource) return;
    const newCategories: Record<string, boolean> = {};
    currentDataSource.line_items
      .filter(item => item.type === 'category_header' && item.category_value)
      .forEach(item => {
        if (item.category_value) {
          newCategories[item.category_value] = false;
        }
      });
    const newState: ExpandedState = {
      sections: { ...expandedState.sections },
      categories: newCategories
    };
    setExpandedState(newState);
  };

  const toggleSection = (section: string) => {
    setExpandedState(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [section]: !prev.sections[section]
      }
    }));
  };

  const toggleCategory = (category: string) => {
    setExpandedState(prev => ({
      ...prev,
      categories: {
        ...prev.categories,
        [category]: !prev.categories[category]
      }
    }));
  };

  // Get period ranges for display
  const getPeriodMonthRanges = () => {
    if (!currentDataSource || viewType === 'monthly') return {};

    const allMonths = [...currentDataSource.periods].sort(compareMonthYearHelper);
    const periods = transformedData?.periods || [];
    
    return periods.reduce((acc, period) => {
      if (viewType === 'yearly') {
        acc[period] = getYearMonthRange(period, allMonths);
      } else if (viewType === 'quarterly') {
        // Calculate quarter month range
        const [q, year] = period.split("'");
        const quarterNum = parseInt(q.slice(1));
        const expectedMonths: string[] = [];
        const startMonth = (quarterNum - 1) * 3;
        for (let i = 0; i < 3; i++) {
          const monthNum = startMonth + i;
          const month = Object.keys(monthMap)[monthNum];
          expectedMonths.push(`${month}-${year}`);
        }
        
        const quarterMonths = allMonths.filter(month => expectedMonths.includes(month));
        if (quarterMonths.length === 3) {
          acc[period] = null;
        } else {
          const firstMonth = quarterMonths[0]?.split('-')[0] || '';
          const lastMonth = quarterMonths[quarterMonths.length - 1]?.split('-')[0] || '';
          acc[period] = firstMonth === lastMonth ? firstMonth : `${firstMonth}-${lastMonth}`;
        }
      }
      return acc;
    }, {} as Record<string, string | null>);
  };

  const periodMonthRanges = getPeriodMonthRanges();

  // TTM period info for yearly view
  const getTTMPeriodInfo = () => {
    if (!currentDataSource || viewType !== 'yearly') return { label: 'TTM', range: null };
    
    const projectPeriodType = selectedProject?.period_type || 'monthly';
    const allPeriods = [...transformedData?.periods || []].sort();
    
    let lookbackPeriods: string[];
    switch (projectPeriodType) {
      case 'quarterly':
        // For quarterly data in yearly view, look back 4 quarters (1 year)
        lookbackPeriods = allPeriods.slice(-1);
        break;
      case 'annual':
        // For annual data, TTM should be the most recent year (closest approximation to trailing twelve months)
        lookbackPeriods = allPeriods.slice(-1);
        break;
      default: // monthly
        // For monthly data in yearly view, look back 12 months (1 year)
        lookbackPeriods = allPeriods.slice(-1);
        break;
    }
    
    if (lookbackPeriods.length === 0) return { label: 'TTM', range: null };
    
    if (projectPeriodType === 'annual' && lookbackPeriods.length > 1) {
      // For annual data with multiple years, show the range
      const oldestYear = lookbackPeriods[0];
      const newestYear = lookbackPeriods[lookbackPeriods.length - 1];
      return {
        label: 'TTM',
        range: oldestYear === newestYear ? null : `${oldestYear}-${newestYear}`
      };
    } else {
      // For single period or other types, no range needed
      return {
        label: 'TTM',
        range: null
      };
    }
  };

  const ttmInfo = getTTMPeriodInfo();

  // Check if we have an active template
  if (!activeTemplate) {
    return (
      <div className="p-8 text-center">
        <p className="text-lg text-gray-600">
          Please select an active template to view the detailed income statement.
        </p>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading P&L data...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-8 text-center">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">{error}</p>
        </div>
      </div>
    );
  }

  if (!transformedData) {
    return (
      <div className="p-8 text-center">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">No P&L data available.</p>
        </div>
      </div>
    );
  }

  const periods = transformedData.periods;
  
  // Group line items by sections for rendering
  const getSectionCategories = (sectionTitle: string) => {
    if (!currentDataSource) return [];
    // Find all category headers for this section
    let foundSection = false;
    return currentDataSource.line_items
      .filter(item => {
        if (item.type === 'section_header' && item.name === sectionTitle) {
          foundSection = true;
          return false;
        }
        if (item.type === 'section_header' && foundSection) {
          foundSection = false;
        }
        return foundSection && item.type === 'category_header' && item.category_value;
      })
      .map(item => item.category_value!);
  };
  const sections = [
    { title: 'Revenue', name: 'Revenue', isRevenue: true, isContraRevenue: false, categories: getSectionCategories('Revenue'), showTotal: true },
    { title: 'Sales Discounts and Returns', name: 'Sales Discounts and Returns', isRevenue: true, isContraRevenue: true, categories: getSectionCategories('Sales Discounts and Returns'), showTotal: true },
    { title: 'Cost of Goods Sold', name: 'Cost of Goods Sold', isRevenue: false, isContraRevenue: false, categories: getSectionCategories('Cost of Goods Sold'), showTotal: true },
    { title: 'Operating Expenses', name: 'Operating Expenses', isRevenue: false, isContraRevenue: false, categories: getSectionCategories('Operating Expenses'), showTotal: true },
    { title: 'Other Expenses', name: 'Other Expenses', isRevenue: false, isContraRevenue: false, categories: getSectionCategories('Other Expenses'), showTotal: true }
  ];

  return (
    <div className="overflow-hidden">
      {/* Add custom styles for auto-fitting columns */}
      <style>
        {`
          .financial-table {
            table-layout: auto;
          }
          
          .financial-table th:first-child,
          .financial-table td:first-child {
            width: 240px;
            min-width: 240px;
            max-width: 240px;
          }

          /* Account number column */
          .financial-table th:nth-child(2) {
            width: auto;
            min-width: 120px;
            max-width: 140px;
            white-space: normal;
            word-break: break-word;
            text-align: center;
          }
          .financial-table td:nth-child(2) {
            width: auto;
            min-width: 120px;
            max-width: 140px;
            white-space: nowrap;
            text-align: center;
          }
          
          .financial-table th:not(:first-child):not(:nth-child(2)),
          .financial-table td:not(:first-child):not(:nth-child(2)) {
            width: auto;
            min-width: 160px;
            max-width: 200px;
          }
      
          .financial-table th.value-column {
            width: auto;
            min-width: 160px;
            max-width: 200px;
          }
        `}
      </style>
      <div className="mb-4 flex justify-end items-center space-x-4">
        {/* Additional debugging before ViewToggle */}
        {(() => {
          console.log('🔍 [Income Statement] About to render ViewToggle with:', {
            selectedProject_id: selectedProject?.id,
            selectedProject_period_type: selectedProject?.period_type,
            currentViewType: viewType
          });
          return null;
        })()}
        <ViewToggle
          currentView={viewType}
          onViewChange={setViewType}
          projectPeriodType={selectedProject?.period_type}
        />
        <button
          onClick={expandAll}
          className={`inline-flex items-center px-3 py-2 border text-sm leading-4 font-medium rounded-md transition-colors ${
            // Check if all categories are expanded
            Object.values(expandedState.sections).every(value => value) &&
            currentDataSource && currentDataSource.line_items.filter(item => item.type === 'category_header' && item.category_value).every(item => expandedState.categories[item.category_value!])
              ? 'text-white bg-blue-600 border-transparent'
              : 'text-blue-700 bg-blue-100 border-blue-300 hover:bg-blue-200'
          }`}
        >
          <ChevronDownSquare className="h-4 w-4 mr-2" />
          Expand All
        </button>
        <button
          onClick={collapseAll}
          className={`inline-flex items-center px-3 py-2 border text-sm leading-4 font-medium rounded-md transition-colors ${
            // Check if all categories are collapsed
            Object.values(expandedState.sections).some(value => value) &&
            currentDataSource && !currentDataSource.line_items.filter(item => item.type === 'category_header' && item.category_value).some(item => expandedState.categories[item.category_value!])
              ? 'text-white bg-blue-600 border-transparent'
              : 'text-blue-700 bg-blue-100 border-blue-300 hover:bg-blue-200'
          }`}
        >
          <ChevronUpSquare className="h-4 w-4 mr-2" />
          Collapse All
        </button>
      </div>
      
      {/* Template info */}
      <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center">
        <span className="mr-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
          Active Template:
        </span>
        <span className="text-sm text-blue-800 font-medium">
          {activeTemplate.name}
        </span>
      </div>
      
      {/* Table container */}
      <div className="overflow-hidden border border-gray-200 rounded-lg">
        <div className="border border-gray-200 rounded-lg">
          <div className="max-h-screen h-128 overflow-auto">
            <div className="inline-block min-w-fit">
              <table className="financial-table divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th scope="col" className="sticky left-0 top-0 z-[110] px-6 py-3 text-left text-sm font-semibold text-black border-r border-b border-gray-200 bg-gray-50">
                      Description
                    </th>
                    <th scope="col" className="sticky top-0 z-[110] px-6 py-3 text-left text-sm font-semibold text-black border-b border-gray-200 bg-gray-50">
                      Account Number
                    </th>
                    {periods.map(period => (
                      <th 
                        key={period} 
                        scope="col" 
                        className="sticky top-0 z-[100] px-6 py-3 text-right text-sm font-semibold text-black border-b border-gray-200 value-column bg-gray-50"
                      >
                        {viewType === 'yearly' ? `CY ${period}` : getDateForPeriod(period, accounts)}
                      </th>
                    ))}
                    {viewType === 'yearly' && (
                      <th className="sticky top-0 z-[100] px-6 py-3 text-right text-sm font-semibold text-black border-b border-gray-200 value-column bg-gray-50">
                        {ttmInfo.range && (
                          <div className="text-xs text-gray-500 mb-1">{ttmInfo.range}</div>
                        )}
                        {ttmInfo.label}
                      </th>
                    )}
                  </tr>
                </thead>
                
                <tbody className="divide-y divide-gray-200">
                  {sections.filter(section => section.categories.length > 0).map((section) => (
                      <React.Fragment key={section.title}>
                        <SectionHeader
                          title={section.title}
                          isOpen={expandedState.sections[section.title]}
                          onClick={() => toggleSection(section.title)}
                          colSpan={periods.length + (viewType === 'yearly' ? 4 : 2)}
                        />
                        {expandedState.sections[section.title] && (
                          <>
                          {section.categories.map(category => (
                            <CategoryRow
                              key={category}
                              category={category}
                              periods={periods}
                              accounts={accounts}
                              viewType={viewType}
                              allMonths={currentDataSource ? currentDataSource.periods : []}
                              isRevenue={section.isRevenue}
                              isContraRevenue={section.isContraRevenue}
                              templateCategories={activeTemplate.categories}
                              isExpanded={!!expandedState.categories[category]}
                              onToggle={() => toggleCategory(category)}
                              selectedProject={selectedProject}
                            />
                          ))}
                          {section.showTotal && (
                            <SectionTotal
                              title={`Total ${section.title}`}
                              periods={periods}
                              total={(period: string) => calculateSectionTotal(
                                accounts,
                                section.categories,
                                period,
                                viewType,
                                section.isRevenue,
                                section.isContraRevenue
                              )}
                              ttmTotal={viewType === 'yearly' ? 
                                calculateTTMSectionTotal(
                                  section.categories,
                                  section.isRevenue,
                                  section.isContraRevenue
                                ) : undefined}
                              viewType={viewType}
                              className="bg-gray-200"
                            />
                          )}
                          {section.title === 'Sales Discounts and Returns' && (
                            <>
                              <SectionTotal
                                title="Net Revenue"
                                periods={periods}
                                total={calculateNetRevenueTotal}
                                ttmTotal={viewType === 'yearly' ? calculateTTMNetRevenue() : undefined}
                                viewType={viewType}
                                className="bg-gray-300"
                              />
                              <tr className="bg-gray-100">
                                <td className="sticky left-0 z-10 bg-gray-100 px-6 py-4 text-sm font-italic text-black border-r border-gray-200">
                                  Net Revenue Growth %
                                </td>
                                <td className="px-6 py-4 text-sm" />
                                {periods.map((period, index) => {
                                  const currentRevenue = calculateNetRevenueTotal(period);
                                  if (index === 0) {
                                    return (
                                      <td key={period} className="px-6 py-4 text-sm text-gray-900 text-right">
                                        n/a
                                      </td>
                                    );
                                  }
                                  const previousPeriod = periods[index - 1];
                                  if (viewType === 'yearly' && period === periods[periods.length - 1]) {
                                return (
                                      <td key={period} className="px-6 py-4 text-sm text-gray-900 text-right">
                                        {calculateYTDGrowthPercentage(
                                          accounts,
                                          ['Sales Revenue'],
                                          ['Sales Returns and Allowances', 'Sales Discounts'],
                                          currentDataSource?.periods || [],
                                          period,
                                          previousPeriod
                                        )}
                                    </td>
                                    );
                                  }
                                  const previousRevenue = calculateNetRevenueTotal(previousPeriod);
                                return (
                                    <td key={period} className="px-6 py-4 text-sm text-gray-900 text-right">
                                      {calculateGrowthPercentage(currentRevenue, previousRevenue)}
                                    </td>
                                  );
                                })}
                                    {viewType === 'yearly' && (
                                  <td className="px-6 py-4 text-sm text-gray-900 text-right">
                                    {calculateTTMYoYGrowth(
                                              accounts,
                                              ['Sales Revenue', 'Sales Returns and Allowances', 'Sales Discounts'],
                                              currentDataSource?.periods || [],
                                              true,
                                              true
                                    )}
                                  </td>
                                )}
                              </tr>
                            </>
                          )}
                          {section.title === 'Cost of Goods Sold' && (
                            <>
                              <SectionTotal
                                title="Gross Margin"
                                periods={periods}
                                total={calculateGrossMargin}
                                ttmTotal={viewType === 'yearly' ? calculateTTMGrossMargin() : undefined}
                                viewType={viewType}
                                className="bg-gray-300"
                              />
                              <tr className="bg-gray-100">
                                <td className="sticky left-0 z-10 bg-gray-100 px-6 py-4 text-sm font-semibold text-black border-r border-gray-200">
                                  Gross Margin % of Net Sales
                                </td>
                                <td className="px-6 py-4 text-sm font-semibold" />
                                {periods.map(period => {
                                  const netSales = calculateNetRevenueTotal(period);
                                  const grossMargin = calculateGrossMargin(period);
                                  const marginPercent = netSales !== 0 ? (grossMargin / netSales) * 100 : 0;
                                  return (
                                    <td key={period} className="px-6 py-4 text-sm font-semibold text-gray-900 text-right">
                                      {marginPercent.toFixed(1)}%
                                    </td>
                                  );
                                })}
                                {viewType === 'yearly' && (
                                  <td className="px-6 py-4 text-sm font-semibold text-gray-900 text-right">
                                    {(() => {
                                            const ttmNetRevenue = calculateTTMNetRevenue();
                                            const ttmGrossMargin = calculateTTMGrossMargin();
                                            const ttmMarginPercent = ttmNetRevenue !== 0 ? 
                                              (ttmGrossMargin / ttmNetRevenue) * 100 : 0;
                                            return `${ttmMarginPercent.toFixed(1)}%`;
                                        })()}
                                      </td>
                                    )}
                                  </tr>
                            </>
                          )}
                          {section.title === 'Operating Expenses' && (
                            <SectionTotal
                              title="Operating Income"
                              periods={periods}
                              total={calculateOperatingIncome}
                              ttmTotal={viewType === 'yearly' ? calculateTTMOperatingIncome() : undefined}
                              viewType={viewType}
                              className="bg-gray-300"
                            />
                          )}
                          </>
                        )}
                      </React.Fragment>
                  ))}
                  {/* Net Income row, shown before the EBITDA Bridge */}
                  <tr className="bg-gray-800 text-white">
                            <td className="sticky left-0 z-10 bg-gray-800 px-6 py-4 text-sm font-semibold border-r border-gray-200">
                      Net Income
                            </td>
                            <td className="px-6 py-4 text-sm font-semibold" />
                    {periods.map(period => (
                              <td key={period} className="px-6 py-4 text-sm font-semibold text-right">
                        {calculateNetIncome(period).toLocaleString('en-US', {
                                  style: 'currency',
                                  currency: 'USD',
                        })}
                              </td>
                            ))}
                            {viewType === 'yearly' && (
                              <td className="px-6 py-4 text-sm font-semibold text-right">
                                {calculateTTMNetIncome().toLocaleString('en-US', {
                                  style: 'currency',
                                  currency: 'USD',
                                })}
                              </td>
                            )}
                          </tr>
                  {/* EBITDABridgeDetailed as a direct child of tbody, matching the old file */}
                  <EBITDABridgeDetailed
                    accounts={accounts}
                    periods={periods}
                    viewType={viewType}
                    allMonths={currentDataSource ? currentDataSource.periods : []}
                    calculateNetIncome={calculateNetIncome}
                    calculateNetRevenueTotal={calculateNetRevenueTotal}
                    ttmNetIncome={viewType === 'yearly' ? calculateTTMNetIncome() : undefined}
                    templateCategories={activeTemplate.categories}
                    calculateTTMNetRevenue={calculateTTMNetRevenue}
                    selectedProject={selectedProject}
                  />
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const UnifiedDetailedIncomeStatement = React.forwardRef<void, UnifiedDetailedIncomeStatementProps>(UnifiedDetailedIncomeStatementInner);

export default UnifiedDetailedIncomeStatement;